import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Style from './About.module.scss';
import { info } from '../../info/Info';

/* ─────────────────────────────────────────
   SCROLL REVEAL — unchanged logic, lazy
   IntersectionObserver setup with a small
   stagger delay per panel
───────────────────────────────────────── */
function useReveal(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let obs = null;

    const timer = setTimeout(() => {
      const el = ref.current;
      if (!el) return;

      obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (obs) obs.disconnect();
          }
        },
        { threshold: 0.08 }
      );

      obs.observe(el);
    }, delay);

    return () => {
      clearTimeout(timer);
      if (obs) obs.disconnect();
    };
  }, [delay]);

  return [ref, visible];
}

/* ─────────────────────────────────────────
   AMBIENT BLOBS — same treatment as Home,
   fixed (not absolute) so they sit behind
   the whole scrollable page rather than
   just the first viewport
───────────────────────────────────────── */
function AmbientBlobs() {
  return (
    <div className={Style.ambientWrap} aria-hidden="true">
      <div className={Style.blobA} />
      <div className={Style.blobB} />
    </div>
  );
}

/* ─────────────────────────────────────────
   PANEL — glass card matching Home's .card
───────────────────────────────────────── */
function Panel({ indexLabel, title, children, delay = 0 }) {
  const [ref, visible] = useReveal(delay);

  return (
    <section
      ref={ref}
      className={Style.panel}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
      }}
    >
      <div className={Style.headerTitle}>
        <span className={Style.headerIndex}>{indexLabel}</span>
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

/* ─────────────────────────────────────────
   SKILL GROUP / CHIP
───────────────────────────────────────── */
function SkillGroup({ label, items }) {
  return (
    <div className={Style.tableRow}>
      <h3 className={Style.tableHead}>{label}</h3>
      <ul className={Style.skillList}>
        {items.map((skill, i) => (
          <li key={i} className={Style.skillItem}>{skill}</li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────
   HOBBY GRID ITEM
───────────────────────────────────────── */
function HobbyCard({ emoji, label }) {
  return (
    <li className={Style.gridItem}>
      <span className={Style.icon} aria-hidden="true">{emoji}</span>
      <span className={Style.text}>{label}</span>
    </li>
  );
}

/* ─────────────────────────────────────────
   ABOUT — MAIN EXPORT
───────────────────────────────────────── */
export default function About({ innerRef }) {
  return (
    <Box
      ref={innerRef}
      component="main"
      id="about"
      className={Style.aboutPage}
    >
      <AmbientBlobs />

      <div className={Style.pageHeader}>
        <div className={Style.eyebrow}>
          <span className={Style.eyebrowLine} aria-hidden="true" />
          <span>Profile</span>
          <span className={Style.eyebrowLine} aria-hidden="true" />
        </div>
        <h1 className={Style.pageTitle}>About</h1>
      </div>

      <div className={Style.panelStack}>
        <Panel indexLabel="§01" title="Professional Summary">
          <p className={Style.paragraph}>{info.bio}</p>
        </Panel>

        <Panel indexLabel="§02" title="Technical Competencies" delay={80}>
          <SkillGroup label="Primary toolset" items={info.skills.proficientWith} />
          <div className={Style.divider} />
          <SkillGroup label="Secondary exposure" items={info.skills.exposedTo} />
        </Panel>

        <Panel indexLabel="§03" title="Interests & Activities" delay={160}>
          <ul className={Style.gridList}>
            {info.hobbies.map((hobby, i) => (
              <HobbyCard key={i} emoji={hobby.emoji} label={hobby.label} />
            ))}
          </ul>
        </Panel>
      </div>
    </Box>
  );
}